import { useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
  Text,
  type Omittable,
} from "@salestrak/ui";

import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { WorkspaceMenuContent } from "./workspace-menu-content";

type WorkspaceSelectorProps = Omittable<{
  /** Solid rounded-rect with tight padding (top-bar style) instead of a fill ghost button. */
  compact?: boolean;
  /** Stretch to fill the available width. */
  fill?: boolean;
  /** Avatar only — hides the workspace name. */
  iconOnly?: boolean;
  /**
   * Natural-width squared trigger on the secondary fill — tile + name +
   * chevron on a soft resting plane one rung above the chrome, with the wash
   * hover. For chrome rows (the nav drawer's header) where the identity has
   * to stand apart from the ghost icon actions beside it; square corners keep
   * it from reading as a field pill.
   */
  soft?: boolean;
}>;

function WorkspaceIdentity({
  centerLabel = false,
  iconOnly = false,
}: {
  centerLabel?: boolean;
  iconOnly?: boolean;
}): ReactElement {
  const { activeWorkspace } = useWorkspace();
  const name = activeWorkspace?.name ?? "Workspace";
  let label: ReactElement | null = null;
  if (!iconOnly) {
    label = (
      <Text size="sm" truncate weight="semibold">
        {name}
      </Text>
    );
    if (centerLabel) {
      label = (
        <Box fill="flex" justify="center" minWidth={0}>
          {label}
        </Box>
      );
    }
  }
  return (
    <>
      <WorkspaceAvatar
        avatarColor={activeWorkspace?.avatarColor}
        logo={activeWorkspace?.settings?.logo}
        name={name}
        size="xs"
      />
      {label}
    </>
  );
}

export function WorkspaceSelector({
  compact = false,
  fill = false,
  iconOnly = false,
  soft = false,
}: WorkspaceSelectorProps = {}): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const trigger = soft ? (
    // Outline, not the neutral solid: the phone drawer paints the `sidebar`
    // gray, and in light mode `secondary` is the same value, so a solid chip
    // vanished. The card fill + hairline is the rung the strip tiles and the
    // Home create button already stand on.
    <Button ink="foreground" shape="square" size="sm" variant="outline" />
  ) : compact ? (
    <Box<"button">
      align="center"
      aria-label="Workspace menu"
      as="button"
      className="h-7 not-dark:bg-[#f0f0f0] dark:bg-secondary"
      direction="row"
      fill={fill ? "width" : undefined}
      gap="sm"
      hoverBackground
      paddingLeft="xs"
      paddingRight={iconOnly ? "xs" : "sm"}
      rounded="md"
    />
  ) : (
    // Full ink: the trigger IS the workspace identity, so the name rests
    // at full ink; the chevron keeps the glyph dim as the secondary affordance.
    <Button
      fullWidth
      ink="foreground"
      justify="start"
      shape="square"
      variant="ghost"
    />
  );

  return (
    <Menu onOpenChange={setMenuOpen} open={menuOpen}>
      <MenuTrigger render={trigger}>
        <WorkspaceIdentity centerLabel={compact && !soft} iconOnly={iconOnly} />
        <Icon color="muted" name="chevron-down" size="md" />
      </MenuTrigger>
      <MenuPopup align="start" padding="none" side="bottom" width="panel">
        <WorkspaceMenuContent onClose={() => setMenuOpen(false)} />
      </MenuPopup>
    </Menu>
  );
}
